import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import produce from 'immer'

import { useToastContext } from '../../../../../contexts/toast'
import {
  DeliveryForecastDespatchAdvice,
  DeliveryForecastDespatchAdviceItem,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../../../data/models'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import {
  requestCompleteDeliveryForecastDespatchAdviceById,
  useAllDeliveryForecastDespatchAdviceItems,
  useDeliveryForecastDespatchAdviceById,
} from '../../../data'
import { AcceptDeliveryForecastDespatchAdviceView } from './AcceptDeliveryForecastDespatchAdvice.view'

type Props = {
  despatchAdviceId: string
  isConfirmationActive: boolean
}

export const AcceptDeliveryForecastDespatchAdviceData: React.FC<Props> = ({
  despatchAdviceId,
  isConfirmationActive,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)

  const { data: deliveryForecastdespatchAdvice } =
    useDeliveryForecastDespatchAdviceById(despatchAdviceId)
  const { data: despatchAdviceItems } =
    useAllDeliveryForecastDespatchAdviceItems(despatchAdviceId)

  const { showToast } = useToastContext()

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const prepareCompletePatchDto = (
    newDeliveryForecastDespatchAdvice: WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>,
    oldDeliveryForecastDespatchAdvice: WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>,
  ): PatchRequest<
    WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
  > => {
    const patchDtos: PatchRequest<
      WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
    > = []

    // ConfirmationCode
    patchDtos.push({
      op: oldDeliveryForecastDespatchAdvice.Payload.ConfirmationCode
        ? 'replace'
        : 'add',
      path: '/Payload/ConfirmationCode',
      value: newDeliveryForecastDespatchAdvice.Payload.ConfirmationCode,
    })

    // ConfirmationDate
    if (
      newDeliveryForecastDespatchAdvice.Payload.ConfirmationDate !==
      oldDeliveryForecastDespatchAdvice.Payload.ConfirmationDate
    ) {
      patchDtos.push({
        op: oldDeliveryForecastDespatchAdvice.Payload.ConfirmationDate
          ? 'replace'
          : 'add',
        path: '/Payload/ConfirmationDate',
        value: newDeliveryForecastDespatchAdvice.Payload.ConfirmationDate,
      })
    }

    return patchDtos
  }

  async function onSubmit() {
    // deactivate the buttons
    setIsRequestRunning(true)

    try {
      // close the dialog and reset
      handleClose()

      // submit form
      await submitForm()

      // navigate back to the start page
      navigate('/delivery-forecasts')

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.title.send.dialog.send.messages.success.summary',
        ),
        detail: t(
          'deliveryForecastDespatchAdvice.title.send.dialog.send.messages.success.detail',
          {
            scheduleAgreementNumber:
              deliveryForecastdespatchAdvice?.Payload.OrderId,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'deliveryForecastDespatchAdvice.title.send.dialog.send.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // submit form
  const submitForm = () => {
    //make a copy of delfor
    const newDeliveryForecastDespatchAdvice = produce(
      deliveryForecastdespatchAdvice!,
      draft => {
        const currentDate = new Date().toISOString()
        draft.Payload.ConfirmationDate = currentDate
        draft.Payload.DeliveryNoteDate = currentDate

        const isItemModified = (item: DeliveryForecastDespatchAdviceItem) =>
          item.ConfirmationCode === 'MODIFIED' ||
          item.ConfirmationCode === 'REJECTED'

        draft.Payload.ConfirmationCode =
          despatchAdviceItems?.some(isItemModified) ||
          deliveryForecastdespatchAdvice?.Payload.ConfirmationCode ===
            'MODIFIED'
            ? 'MODIFIED'
            : 'ACCEPTED'
      },
    )

    //patch
    const response = requestCompleteDeliveryForecastDespatchAdviceById(
      despatchAdviceId,
      prepareCompletePatchDto(
        newDeliveryForecastDespatchAdvice,
        deliveryForecastdespatchAdvice!,
      ),
    )
    return response
  }

  // handleClose
  const handleClose = () => {
    setVisible(false)
  }

  return (
    <AcceptDeliveryForecastDespatchAdviceView
      despatchAdviceId={despatchAdviceId}
      visible={visible}
      setVisible={setVisible}
      handleClose={handleClose}
      isRequestRunning={isRequestRunning}
      onSubmit={onSubmit}
      isConfirmationActive={isConfirmationActive}
    />
  )
}
